import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

import { SEARCH_INPUT_DELAY, TranslationContext } from "@is/constants";
import { CollectionStyle, CollectionStylesResponse } from "@is/types";

type Props = {
  onStylesGet: (
    term: string,
    includeOutOfStockItems: boolean,
    page?: number
  ) => Promise<CollectionStylesResponse>;
  onSearchFocus?: () => void;
  includedVariants: string[];
  isPending?: boolean;
};

const useManageSearchStoreFavorites = ({
  onStylesGet,
  onSearchFocus,
  includedVariants,
  isPending = false,
}: Props) => {
  const { t } = useTranslation(TranslationContext.Settings);

  const [value, setValue] = useState<string>("");
  const [styles, setStyles] = useState<CollectionStyle[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalStyles, setTotalStyles] = useState<number>(0);
  const [showOnlyInStock, setShowOnlyInStock] = useState<boolean>(true);

  const [debouncedValue] = useDebounce(value.trim(), SEARCH_INPUT_DELAY, {
    equalityFn: (prev, next) => prev === next || next?.length === 1,
  });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setValue("");
    setStyles([]);
    setPage(1);
    setTotalStyles(0);
  }, []);

  const handleStylesGet = useCallback(async () => {
    const res = await onStylesGet(debouncedValue, !showOnlyInStock);

    setStyles(res.items);
    setPage(res.page);
    setTotalStyles(res.totalCount);
  }, [debouncedValue, onStylesGet, showOnlyInStock]);

  const handleShowNextPage = useCallback(async () => {
    const res = await onStylesGet(debouncedValue, !showOnlyInStock, page + 1);

    setStyles((styles) => [...styles, ...res.items]);
    setPage(res.page);
    setTotalStyles(res.totalCount);
  }, [debouncedValue, onStylesGet, showOnlyInStock, page]);

  const handleShowOnlyInStockChange = useCallback(() => {
    setShowOnlyInStock((prev) => !prev);
  }, []);

  const handleInputOnFocus = useCallback(() => {
    if (onSearchFocus) {
      onSearchFocus();
    }
  }, [onSearchFocus]);

  const showShowMoreButton = useMemo(() => {
    return value && totalStyles > styles.length;
  }, [value, styles, totalStyles]);

  const pending = useMemo(() => {
    if (isPending && !styles.length && page === 1 && debouncedValue?.length) {
      return true;
    }
    return false;
  }, [isPending, page, styles, debouncedValue]);

  useEffect(() => {
    setStyles((styles) =>
      styles.map((style) => ({
        ...style,
        isAddedToCollection: includedVariants.includes(style.variantId),
      }))
    );
  }, [includedVariants]);

  useEffect(() => {
    if (debouncedValue?.length) {
      handleStylesGet();
    } else {
      handleReset();
    }
  }, [debouncedValue, handleStylesGet, handleReset]);

  return {
    t,
    styles,
    pending,
    handleChange,
    handleReset,
    handleShowOnlyInStockChange,
    handleShowNextPage,
    showOnlyInStock,
    value,
    showShowMoreButton,
    handleInputOnFocus,
  };
};

export default useManageSearchStoreFavorites;
